/**
 * usePreferenceLoader Hook
 *
 * Handles automatic preference loading from server after user login.
 * Encapsulates the complete flow:
 * 1. Request notification permission
 * 2. Initialize service worker
 * 3. Subscribe to push notifications
 * 4. Gather device information
 * 5. Register device with backend
 * 6. Update local state
 *
 * Design: Non-blocking, silent failures, idempotent
 */

import { useCallback, useRef, useState } from 'react';
import { useHttpClient, useAuth, UserPreferencesService } from '@actschurch/ccms-sdk';
import type { UserPreferences } from '@actschurch/ccms-sdk';
import { usePreferencesStore } from '@/stores/preferencesStore';

interface PreferenceLoaderState {
  isLoading: boolean;
  isLoaded: boolean;
  error: string | null;
  serverPrefsExist: boolean;
}

export function usePreferenceLoader() {
  const httpClient = useHttpClient();
  const { state: authState } = useAuth();
  const loadAttempted = useRef(false);
  const [isLoadingPreferences, setIsLoadingPreferences] = useState(false);

  const updatePreferences = usePreferencesStore((state) => state.updatePreferences);
  const lastSyncTime = usePreferencesStore((state) => state.lastSyncTime);

  /**
   * Check if local preferences are "empty" (only defaults)
   * Strategy: Check if lastSyncTime is a valid timestamp (> 0)
   * - undefined or 0 = default preferences (never synced)
   * - valid timestamp = user has preferences from server or onboarding
   */
  const hasLocalPreferences = useCallback((): boolean => {
    return lastSyncTime !== undefined && lastSyncTime > 0;
  }, [lastSyncTime]);

  /**
   * Load preferences from server
   * Safe to call multiple times - idempotent
   */
  const loadPreferences = useCallback(async (): Promise<PreferenceLoaderState> => {
    const result: PreferenceLoaderState = {
      isLoading: true,
      isLoaded: false,
      error: null,
      serverPrefsExist: false,
    };

    try {
      // STEP 1: Validate prerequisites
      if (!authState.isAuthenticated || !authState.user) {
        console.warn('[PreferenceLoader] User not authenticated, skipping');
        return { ...result, isLoading: false, error: 'Not authenticated' };
      }

      // STEP 2: Fetch server preferences using SDK's UserPreferencesService (userId from token)
      console.log('[PreferenceLoader] Fetching preferences from server...', {
        hasLocalPreferences: hasLocalPreferences(),
        lastSyncTime,
      });
      const userPreferencesService = new UserPreferencesService(httpClient);

      let serverPrefs: UserPreferences;
      try {
        serverPrefs = await userPreferencesService.getMyPreferencesByApp('2'); // appId = '2' for ccms_vendors, userId from token
        result.serverPrefsExist = true;
      } catch (error: any) {
        // No server preferences found (404) - this is OK for new users
        // Check both status code and error code (CCMSError uses 'NOT_FOUND' code for 404)
        const is404Error =
          error.status === 404 ||
          error.response?.status === 404 ||
          error.code === 'NOT_FOUND';

        if (is404Error) {
          console.log('[PreferenceLoader] No server preferences found (new user)');
          return {
            ...result,
            isLoading: false,
            isLoaded: true,
            serverPrefsExist: false,
          };
        }
        throw error; // Re-throw other errors
      }

      // STEP 3: Decide whether to load server preferences
      const hasLocal = hasLocalPreferences();

      if (!hasLocal) {
        // No local preferences - load from server unconditionally
        console.log('[PreferenceLoader] No local preferences, loading from server');
      } else {
        // Both exist - compare timestamps (newest wins)
        const serverTime = serverPrefs.modifiedDateTime || serverPrefs.createdDateTime || 0;
        const localTime = lastSyncTime || 0;

        console.log('[PreferenceLoader] Comparing timestamps:', {
          serverTime,
          localTime,
          serverDate: new Date(serverTime * 1000).toISOString(),
          localDate: new Date(localTime * 1000).toISOString(),
        });

        if (serverTime <= localTime) {
          console.log('[PreferenceLoader] Local preferences are newer or equal, skipping load');
          return {
            ...result,
            isLoading: false,
            isLoaded: true,
            serverPrefsExist: true,
          };
        }

        console.log('[PreferenceLoader] Server preferences are newer, merging');
      }

      // STEP 4: Parse server preferences JSON
      const prefsData = JSON.parse(serverPrefs.preferences);

      // STEP 5: Merge into Zustand store
      updatePreferences({
        ...prefsData,
        lastSyncTime: serverPrefs.modifiedDateTime || serverPrefs.createdDateTime,
      });

      console.log('[PreferenceLoader] Preferences loaded successfully!');
      return {
        isLoading: false,
        isLoaded: true,
        error: null,
        serverPrefsExist: true,
      };
    } catch (error: any) {
      // Silent failure - log error but don't block user
      console.error('[PreferenceLoader] Load failed:', error);

      return {
        isLoading: false,
        isLoaded: false,
        error: error.message || 'Unknown error',
        serverPrefsExist: false,
      };
    }
  }, [httpClient, authState, hasLocalPreferences, lastSyncTime, updatePreferences]);

  /**
   * Auto-load preferences on login
   * Call this from onLogin callback
   * Non-blocking - runs in background
   */
  const autoLoadOnLogin = useCallback(async (): Promise<PreferenceLoaderState> => {
    // Prevent double loading
    if (loadAttempted.current) {
      console.log('[PreferenceLoader] Load already attempted this session');
      return {
        isLoading: false,
        isLoaded: true,
        error: null,
        serverPrefsExist: false,
      };
    }

    loadAttempted.current = true;
    setIsLoadingPreferences(true);

    // Small delay to ensure auth state is settled
    return new Promise((resolve) => {
      setTimeout(() => {
        loadPreferences()
          .then((result) => {
            setIsLoadingPreferences(false);
            resolve(result);
          })
          .catch((error) => {
            console.error('[PreferenceLoader] Auto-load failed:', error);
            setIsLoadingPreferences(false);
            // Silent failure - user can still use app
            resolve({
              isLoading: false,
              isLoaded: false,
              error: error.message,
              serverPrefsExist: false,
            });
          });
      }, 500); // 500ms delay (shorter than device registration's 1000ms)
    });
  }, [loadPreferences]);

  /**
   * Reset load attempt flag
   * Call on logout to allow re-loading on next login
   */
  const resetLoader = useCallback(() => {
    loadAttempted.current = false;
    setIsLoadingPreferences(false);
  }, []);

  return {
    loadPreferences,
    autoLoadOnLogin,
    resetLoader,
    hasLocalPreferences: hasLocalPreferences(),
    isLoadingPreferences,
  };
}
